import { Badge, Banner, Button } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useInstagramConnect } from '../../hooks/useInstagramConnect';
import { BrandLogo } from '../BrandLogo';
import { SkeletonText } from '../../components/ui/Skeleton';

/** Conta do Instagram (GET /api/tools/instagram → accounts[]) */
export interface InstagramAccount {
  id: string; username: string; name: string | null; profilePictureUrl: string | null; status: string;
  tokenExpiresAt: string | null; tokenRefreshError: string | null;
}

export function InstagramStatusBadge({ status }: { status: string }) {
  if (status === 'CONNECTED') return <Badge variant="green">Conectada</Badge>;
  if (status === 'ERROR') return <Badge variant="red">Reconecte</Badge>;
  return <Badge variant="neutral">Desconectada</Badge>;
}

/**
 * Contas do Instagram em WhatsApp › Conexões: lista e atalho para conectar.
 * Automações, ice breakers e desconexão ficam em Ferramentas › Instagram.
 */
export function InstagramAccounts({ search = '', onLoaded }: { search?: string; onLoaded?: (accounts: InstagramAccount[]) => void }) {
  const { can } = useTenant();
  const fetchApi = useAuthenticatedFetch();
  const connect = useInstagramConnect();
  const [data, setData] = useState<{ configured: boolean; accounts: InstagramAccount[] } | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    const res = await fetchApi('/api/tools/instagram').catch(() => null);
    if (!res?.ok) { setFailed(true); return; }
    const d = await res.json();
    setData(d);
    setFailed(false);
    onLoaded?.(d.accounts || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  if (failed) return <Banner variant="error" description={<>Não foi possível carregar as contas do Instagram.</>} />;
  if (!data) return <div className="py-8"><SkeletonText lines={4} /></div>;

  const q = search.trim().toLowerCase();
  const visible = q ? data.accounts.filter((a) => [a.username, a.name].some((v) => v && v.toLowerCase().includes(q))) : data.accounts;

  return (
    <div className="space-y-3">
      {!data.configured && <Banner variant="default" description={<>O app do Instagram ainda não foi configurado pelo administrador da plataforma.</>} />}
      {data.accounts.length === 0 ? (
        <div className="brand-card flex items-center justify-between gap-4 flex-wrap">
          <div style={{ color: 'var(--text-muted)' }}>Nenhuma conta conectada. Use uma conta Empresa ou Criador de conteúdo.</div>
          {data.configured && can('connections:manage') && (
            <span className="flex items-center gap-2">
              <BrandLogo brand="instagram" size={16} rounded={4} />
              <Button variant="primary" onClick={connect}>Conectar Instagram</Button>
            </span>
          )}
        </div>
      ) : visible.length === 0 ? (
        <div style={{ color: 'var(--text-muted)' }}>Nenhuma conta do Instagram corresponde à busca.</div>
      ) : (
        visible.map((a) => (
          <div key={a.id} className="brand-card flex items-center justify-between gap-4">
            <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
              {a.profilePictureUrl ? <img src={a.profilePictureUrl} alt="" width={40} height={40} style={{ borderRadius: 20 }} /> : <BrandLogo brand="instagram" size={40} rounded={20} />}
              <div style={{ minWidth: 0 }}>
                <div className="flex items-center gap-2">
                  <div className="font-semibold">@{a.username}</div>
                  <InstagramStatusBadge status={a.status} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {a.name || ''}{a.tokenExpiresAt ? ` · acesso renovado até ${new Date(a.tokenExpiresAt).toLocaleDateString('pt-BR')}` : ''}
                  {a.tokenRefreshError ? ` · ${a.tokenRefreshError}` : ''}
                </div>
              </div>
            </div>
            <Link to="/ferramentas/instagram" style={{ flexShrink: 0 }}><Button variant="secondary">Gerenciar</Button></Link>
          </div>
        ))
      )}
    </div>
  );
}
